import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  LATEST_TXN_DATES_QUERY,
  SALES_TARGET_HISTORY_QUERY,
  STORES_DETAIL_QUERY,
  TRANSACTIONS_QUERY,
} from '../graphql/queries';
import { useSelectedStore } from '../hooks/useSelectedStore';
import { eventFor, useCalendarEvents } from '../hooks/useCalendarEvents';
import { gql } from '../lib/api';
import { airRegiAccountHour, airRegiBusinessDate, airRegiService, type AirRegiTransactionTime } from '../lib/airRegiBusinessTime';
import { type StoreLD } from '../lib/salesAnalytics';
import {
  COMPANY_MONTHLY_SALES_TARGET,
  STORE_MONTHLY_SALES_TARGETS,
  hourlySalesTarget,
  segmentMonthlyTarget,
} from '../lib/salesTarget';

interface Store extends StoreLD {
  name: string;
  airregiBusinessDayStart: string | null;
}

interface Transaction extends AirRegiTransactionTime {
  total: number;
  customerCount: number | null;
}

type Segment = 'all' | 'lunch' | 'dinner';

interface DisplayOption {
  key: string;
  store: Store;
  segment: Segment;
  label: string;
}

interface DailyPoint {
  date: string;
  dateLabel: string;
  day: string;
  sales: number;
  customers: number;
}

interface WeeklySeries {
  from: string;
  to: string;
  points: DailyPoint[];
  sales: number;
  customers: number;
  weekday: string;
  supplemented: boolean;
  targetDate: string;
  monthlyTarget: number;
  dailyTarget: number;
  hourlyTarget: number;
  operatingDays: number;
  eventTitle?: string;
}

interface MonthlySummary {
  periodKey: string;
  lunchSales: number | null;
  dinnerSales: number | null;
}

const DAY_LABELS = ['火', '水', '木', '金', '土', '日'] as const;

const toDate = (date: string): Date => new Date(`${date}T00:00:00Z`);

const toDateString = (date: Date): string => date.toISOString().slice(0, 10);

const addDays = (date: string, amount: number): string => {
  const next = toDate(date);
  next.setUTCDate(next.getUTCDate() + amount);
  return toDateString(next);
};

const todayInJapan = (): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
};

const businessWeekRange = (latestDate: string): { from: string; to: string } => {
  const latest = toDate(latestDate);
  const daysFromTuesday = (latest.getUTCDay() + 5) % 7;
  const from = addDays(latestDate, -daysFromTuesday);
  return { from, to: addDays(from, 5) };
};

const shortDate = (date: string): string => {
  const [, month, day] = date.split('-');
  return `${month}/${day}`;
};

const longDate = (date: string): string => {
  const [year, month, day] = date.split('-');
  return `${year}年${month}月${day}日`;
};

const fmtYen = (value: number): string => `¥${Math.round(value).toLocaleString()}`;

const addMonths = (yearMonth: string, amount: number): string => {
  const [year, month] = yearMonth.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1 + amount, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
};

const isDateString = (value: string | null): value is string => /^\d{4}-\d{2}-\d{2}$/.test(value ?? '');

const displayOptionsFor = (stores: Store[]): DisplayOption[] =>
  [...stores]
    .sort((a, b) => Number(!!b.lunchStart) - Number(!!a.lunchStart))
    .flatMap<DisplayOption>((store): DisplayOption[] => {
      if (store.lunchStart && store.lunchEnd) {
        return [
          { key: `${store.id}:lunch`, store, segment: 'lunch', label: `${store.name}（ランチ）` },
          { key: `${store.id}:dinner`, store, segment: 'dinner', label: `${store.name}（ディナー）` },
        ];
      }
      return [{ key: `${store.id}:all`, store, segment: 'all', label: store.name }];
    });

export const WeeklySalesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedStoreId, setStoredStoreId] = useSelectedStore();
  const initialStoreId = searchParams.get('store') || storedStoreId;
  const initialSegment = searchParams.get('segment');
  const initialDate = searchParams.get('date');
  const [selectedKey, setSelectedKey] = useState(
    `${initialStoreId}:${initialSegment === 'lunch' || initialSegment === 'dinner' ? initialSegment : 'all'}`,
  );
  const [selectedDate, setSelectedDate] = useState(isDateString(initialDate) ? initialDate : '');

  const storesQ = useQuery({
    queryKey: ['storesDetail'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_DETAIL_QUERY),
  });
  const stores = useMemo(() => storesQ.data?.stores ?? [], [storesQ.data?.stores]);
  const displayOptions = useMemo(() => displayOptionsFor(stores), [stores]);
  const requestedStoreId = selectedKey.slice(0, selectedKey.lastIndexOf(':'));
  const selectedOption = displayOptions.find((option) => option.key === selectedKey)
    ?? displayOptions.find((option) => option.store.id === requestedStoreId)
    ?? displayOptions[0];
  const effectiveStoreId = selectedOption?.store.id ?? '';
  const effectiveSegment = selectedOption?.segment ?? 'all';
  const hasLunchService = Boolean(selectedOption?.store.lunchStart && selectedOption.store.lunchEnd);

  useEffect(() => {
    if (!selectedOption) return;
    if (selectedKey !== selectedOption.key) setSelectedKey(selectedOption.key);
    setStoredStoreId(effectiveStoreId);
  }, [effectiveStoreId, selectedKey, selectedOption, setStoredStoreId]);

  useEffect(() => {
    if (!effectiveStoreId) return;
    const next = new URLSearchParams(searchParams);
    next.set('store', effectiveStoreId);
    if (effectiveSegment === 'all') next.delete('segment');
    else next.set('segment', effectiveSegment);
    if (selectedDate) next.set('date', selectedDate);
    else next.delete('date');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [effectiveSegment, effectiveStoreId, searchParams, selectedDate, setSearchParams]);

  const probeRange = useMemo(() => {
    const to = todayInJapan();
    return { from: addDays(to, -60), to };
  }, []);

  const latestQ = useQuery({
    enabled: !!effectiveStoreId,
    queryKey: ['weeklySalesLatest', effectiveStoreId, effectiveSegment, stores, probeRange.to],
    queryFn: async () => {
      for (let offset = 0; offset <= 60; offset += 7) {
        const results = await Promise.all(Array.from({ length: Math.min(7, 61 - offset) }, (_, i) => {
          const date = addDays(probeRange.to, -offset - i);
          return gql<{ transactions: AirRegiTransactionTime[] }>(LATEST_TXN_DATES_QUERY, {
            storeId: effectiveStoreId, from: date, to: date,
          });
        }));
        const store = stores.find((item) => item.id === effectiveStoreId);
        const transactions = results.flatMap((r) => r.transactions).filter((tx) =>
          effectiveSegment === 'all' || airRegiService(tx, store) === effectiveSegment);
        if (transactions.length) return { transactions };
      }
      return { transactions: [] };
    },
  });

  const latestDate = useMemo(() => {
    const store = stores.find((item) => item.id === effectiveStoreId);
    const dates = latestQ.data?.transactions
      .filter((transaction) =>
        effectiveSegment === 'all' || airRegiService(transaction, store) === effectiveSegment)
      .map((transaction) => airRegiBusinessDate(transaction, store?.airregiBusinessDayStart)) ?? [];
    return dates.sort().at(-1) ?? '';
  }, [effectiveSegment, effectiveStoreId, latestQ.data, stores]);
  const latestCompleteDate = useMemo(() => {
    if (!latestDate) return '';
    const range = businessWeekRange(latestDate);
    return range.to;
  }, [latestDate]);

  useEffect(() => {
    if (!selectedDate && latestCompleteDate) setSelectedDate(latestCompleteDate);
  }, [latestCompleteDate, selectedDate]);

  const selectedWeekRange = useMemo(
    () => (selectedDate ? businessWeekRange(selectedDate) : null),
    [selectedDate],
  );
  const sixWeekRange = useMemo(
    () => selectedWeekRange
      ? { from: addDays(selectedWeekRange.from, -7), to: selectedWeekRange.to }
      : null,
    [selectedWeekRange],
  );
  const eventsQ = useCalendarEvents(sixWeekRange?.from, sixWeekRange?.to);

  const historyRange = useMemo(() => {
    if (!selectedWeekRange) return null;
    const selectedMonth = selectedWeekRange.from.slice(0, 7);
    return { from: addMonths(selectedMonth, -12), to: addMonths(selectedMonth, -1) };
  }, [selectedWeekRange]);

  const ratioQ = useQuery({
    enabled: !!historyRange,
    queryKey: ['weeklySalesTargetRatio', 'loop-hamburg', historyRange?.from, historyRange?.to],
    queryFn: () => gql<{ monthlySummary: MonthlySummary[] }>(SALES_TARGET_HISTORY_QUERY, {
      storeId: 'loop-hamburg',
      fromYearMonth: historyRange!.from,
      toYearMonth: historyRange!.to,
    }),
  });
  const historicalRatio = useMemo(() => {
    const summaries = ratioQ.data?.monthlySummary ?? [];
    const lunch = summaries.reduce((sum, month) => sum + (Number(month.lunchSales) || 0), 0);
    const dinner = summaries.reduce((sum, month) => sum + (Number(month.dinnerSales) || 0), 0);
    return {
      lunch: lunch + dinner > 0 ? lunch / (lunch + dinner) : 0.5,
      months: summaries.filter((month) => (month.lunchSales ?? 0) + (month.dinnerSales ?? 0) > 0).length,
    };
  }, [ratioQ.data]);

  const transactionsQ = useQuery({
    enabled: !!effectiveStoreId && !!sixWeekRange,
    queryKey: ['weeklySalesKpi', effectiveStoreId, sixWeekRange?.from, sixWeekRange?.to],
    queryFn: async () => {
      const transactions: Transaction[] = [];
      for (let offset = 0; offset < 14; offset += 7) {
        const results = await Promise.all(Array.from({ length: 7 }, (_, i) => {
          const date = addDays(sixWeekRange!.from, offset + i);
          return gql<{ transactions: Transaction[] }>(TRANSACTIONS_QUERY, {
            storeId: effectiveStoreId, from: date, to: date,
          });
        }));
        transactions.push(...results.flatMap((r) => r.transactions));
      }
      return { transactions };
    },
  });

  const weeklySeries = useMemo<WeeklySeries[]>(() => {
    if (!selectedWeekRange) return [];
    const store = stores.find((s) => s.id === effectiveStoreId);
    const businessDate = (transaction: Transaction) =>
      airRegiBusinessDate(transaction, store?.airregiBusinessDayStart);
    const accountHour = (transaction: Transaction) =>
      airRegiAccountHour(transaction, store?.airregiBusinessDayStart);
    const matching = (transactionsQ.data?.transactions ?? []).filter((tx) =>
      effectiveSegment === 'all' || airRegiService(tx, store) === effectiveSegment);
    const startTime = effectiveSegment === 'lunch' ? store?.lunchStart : store?.dinnerStart;
    const endTime = effectiveSegment === 'lunch' ? store?.lunchEnd : store?.dinnerEnd;
    const startHour = startTime ? Number(startTime.split(':')[0]) : 0;
    const endHour = endTime ? Math.ceil(Number(endTime.split(':')[0]) + Number(endTime.split(':')[1]) / 60) : 24;
    const days = DAY_LABELS.map((weekday, dayIndex) => {
      const selected = addDays(selectedWeekRange.from, dayIndex);
      const event = eventFor(eventsQ.data?.calendarEvents, effectiveStoreId, selected);
      const current = matching.filter((tx) => businessDate(tx) === selected);
      const priorDate = addDays(selected, -7);
      const priorEvent = eventFor(eventsQ.data?.calendarEvents, effectiveStoreId, priorDate);
      const prior = matching.filter((tx) => businessDate(tx) === priorDate);
      const supplemented = !event && !priorEvent && !current.length && prior.length > 0;
      const date = supplemented ? addDays(selected, -7) : selected;
      const txns = event ? [] : supplemented ? prior : current;
      return { selected, event, supplemented, date, txns, weekday };
    });
    const plottedHours = days.flatMap(({ txns }) => txns.map(accountHour).filter((hour): hour is number => hour !== null));
    const firstHour = Math.min(startHour, ...plottedHours);
    const lastHour = Math.max(endHour - 1, ...plottedHours) + 2;
    return days.map(({ selected, event, supplemented, date, txns, weekday }) => {
      const hourCount = Math.max(1, endHour - startHour);
      const target = hourlySalesTarget({
        storeId: effectiveStoreId,
        segment: effectiveSegment,
        date: selected,
        lunchRatio: historicalRatio.lunch,
        hourCount,
      });
      const points = Array.from({ length: lastHour - firstHour + 1 }, (_, i) => {
        const hour = firstHour + i;
        const hourly = txns.filter((tx) => accountHour(tx) === hour);
        return { date, dateLabel: shortDate(date), day: `${String(hour).padStart(2, '0')}:00`,
          sales: hourly.reduce((sum, tx) => sum + (Number(tx.total) || 0), 0),
          customers: hourly.reduce((sum, tx) => sum + (Number(tx.customerCount) || 0), 0) };
      });
      return { from: date, to: date, targetDate: selected, weekday, supplemented, points,
        eventTitle: event?.title,
        monthlyTarget: target.monthly, dailyTarget: target.daily,
        hourlyTarget: target.hourly, operatingDays: target.operatingDays,
        sales: points.reduce((sum, p) => sum + p.sales, 0),
        customers: points.reduce((sum, p) => sum + p.customers, 0) };
    });
  }, [effectiveSegment, effectiveStoreId, eventsQ.data, historicalRatio.lunch, selectedWeekRange, stores, transactionsQ.data]);

  const latestWeek = weeklySeries.length ? {
    sales: weeklySeries.reduce((sum, day) => sum + day.sales, 0),
    customers: weeklySeries.reduce((sum, day) => sum + day.customers, 0),
    points: weeklySeries.map((day) => ({
      date: day.from,
      targetDate: day.targetDate,
      dateLabel: shortDate(day.from),
      day: day.weekday,
      sales: day.sales,
      customers: day.customers,
      dailyTarget: day.dailyTarget,
      targetGap: day.sales - day.dailyTarget,
      achieved: day.sales >= day.dailyTarget,
      eventTitle: day.eventTitle,
    })),
  } : undefined;
  const eligibleDays = weeklySeries.filter((day) => !day.eventTitle);
  const weeklyTarget = eligibleDays.reduce((sum, day) => sum + day.dailyTarget, 0);
  const weeklyGap = (latestWeek?.sales ?? 0) - weeklyTarget;
  const weeklyAchievementRate = weeklyTarget > 0 ? ((latestWeek?.sales ?? 0) / weeklyTarget) * 100 : 0;
  const achievedDays = eligibleDays.filter((day) => day.sales >= day.dailyTarget).length;
  const supplementedDays = weeklySeries.filter((day) => day.supplemented).length;
  const weeklyAchieved = eligibleDays.length > 0 && weeklyGap >= 0;
  const maxDailySales = Math.max(0, ...weeklySeries.flatMap((week) => [
    week.hourlyTarget,
    ...week.points.map((point) => point.sales),
  ]));
  const maxDailyCustomers = Math.max(0, ...weeklySeries.flatMap((week) => week.points.map((point) => point.customers)));
  const salesAxisMax = Math.max(10_000, Math.ceil(maxDailySales / 10_000) * 10_000);
  const customersAxisMax = Math.max(10, Math.ceil(maxDailyCustomers / 10) * 10);
  const isLoading = storesQ.isLoading || latestQ.isLoading || transactionsQ.isLoading || ratioQ.isLoading || eventsQ.isLoading;
  const hasError = storesQ.isError || latestQ.isError || transactionsQ.isError || eventsQ.isError;

  return (
    <div className="kpi-weekly-page">
      <header className="kpi-weekly-heading">
        <div>
          <div className="kpi-weekly-kicker">KPI LAB / WEEKLY PULSE</div>
          <h1>週間売上</h1>
          <p>火曜日から日曜日まで、1日1グラフで時間別の売上と来客数を比較します。</p>
        </div>
        <label className="kpi-weekly-store">
          <span>表示店舗・時間帯</span>
          <select value={selectedOption?.key ?? ''} onChange={(event) => setSelectedKey(event.target.value)}>
            {displayOptions.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>
        </label>
      </header>

      <div className="kpi-weekly-note">
        <span>KPI検討用</span>
        <p>売上はAirレジと同じ来店日時・営業日切替{selectedOption?.store.airregiBusinessDayStart ?? '05:00'}を基準に集計します。ランチとディナーは店舗マスタの集計終点で分け、閉店予定時刻を過ぎた会計も含めます。日跨ぎ分は24:00以降に置き、横軸は最終会計から2時間先まで表示します。データがない曜日は前週分で補い、イベント日はKPIから除外します。</p>
      </div>

      <div className={`kpi-sales-target-strip${hasLunchService ? '' : ' kpi-sales-target-strip-two'}`}>
        <div>
          <span>全社月間最低目標</span>
          <strong>{fmtYen(COMPANY_MONTHLY_SALES_TARGET)}</strong>
          <small>好EN {fmtYen(STORE_MONTHLY_SALES_TARGETS.koen)} ／ LOOP {fmtYen(STORE_MONTHLY_SALES_TARGETS['loop-hamburg'])}</small>
        </div>
        <div>
          <span>表示中の月間目標</span>
          <strong>{fmtYen(segmentMonthlyTarget(effectiveStoreId, effectiveSegment, historicalRatio.lunch))}</strong>
          <small>{selectedOption?.label}</small>
        </div>
        {hasLunchService && (
          <div>
            <span>LOOP 売上配分</span>
            <strong>ランチ {(historicalRatio.lunch * 100).toFixed(1)}% ／ ディナー {((1 - historicalRatio.lunch) * 100).toFixed(1)}%</strong>
            <small>
              {historicalRatio.months > 0
                ? `${historyRange?.from}〜${historyRange?.to}（${historicalRatio.months}か月）の実績比率`
                : '参照できる実績がないため50%ずつで仮配分'}
            </small>
          </div>
        )}
      </div>

      <div className="kpi-weekly-range-controls">
        <label>
          <span>表示する週の基準日</span>
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
          />
        </label>
        <div className="kpi-weekly-range-actions">
          <button type="button" disabled={!selectedDate} onClick={() => setSelectedDate(addDays(selectedDate, -7))}>← 前の週</button>
          <button type="button" disabled={!selectedDate} onClick={() => setSelectedDate(addDays(selectedDate, 7))}>次の週 →</button>
          <button type="button" disabled={!latestCompleteDate} onClick={() => setSelectedDate(latestCompleteDate)}>最新週</button>
        </div>

      </div>

      {hasError && (
        <section className="kpi-weekly-state" role="alert">
          データを取得できませんでした。時間をおいて再度お試しください。
        </section>
      )}

      {!hasError && isLoading && (
        <section className="kpi-weekly-state">週間データを読み込んでいます…</section>
      )}

      {!hasError && !isLoading && !latestDate && (
        <section className="kpi-weekly-state">この店舗の直近60日間に売上データがありません。</section>
      )}

      {!hasError && !isLoading && latestDate && selectedWeekRange && latestWeek && (
        <>
          <section className="kpi-weekly-overview">
            <div className="kpi-weekly-period">
              <span>対象営業週</span>
              <strong>{longDate(selectedWeekRange.from)} — {longDate(selectedWeekRange.to)}</strong>
              <small>{selectedOption?.label} ／ データ最新日 {longDate(latestDate)}</small>
            </div>
            <div className="kpi-weekly-total kpi-weekly-total-sales">
              <span>集計対象{eligibleDays.length}日間の売上</span>
              <strong>{fmtYen(latestWeek.sales)}</strong>
              <small>イベント日を除く、前週補完分を含む合計</small>
            </div>
            <div className="kpi-weekly-total kpi-weekly-total-customers">
              <span>集計対象{eligibleDays.length}日間の来客数</span>
              <strong>{latestWeek.customers.toLocaleString()}<em>人</em></strong>
              <small>イベント日を除く、前週補完分を含む合計</small>
            </div>
          </section>

          <section className={`kpi-weekly-summary ${weeklyAchieved ? 'kpi-weekly-summary-achieved' : 'kpi-weekly-summary-below'}`}>
            <header>
              <div>
                <span>WEEKLY SUMMARY</span>
                <h2>集計対象{eligibleDays.length}日間の週間サマリー</h2>
              </div>
              <div className="kpi-weekly-summary-status">
                <strong>{eligibleDays.length === 0 ? '判定対象なし' : weeklyAchieved ? '✓ 週間達成' : '△ 週間未達'}</strong>
                <small>
                  {supplementedDays > 0
                    ? `前週補完${supplementedDays}日を含む暫定判定`
                    : `集計対象${eligibleDays.length}日間の実績で判定`}
                </small>
              </div>
            </header>
            <dl>
              <div><dt>売上実績</dt><dd>{fmtYen(latestWeek.sales)}</dd></div>
              <div><dt>週間目標</dt><dd>{fmtYen(weeklyTarget)}</dd></div>
              <div>
                <dt>目標差</dt>
                <dd className={weeklyGap >= 0 ? 'kpi-weekly-summary-positive' : 'kpi-weekly-summary-negative'}>
                  {weeklyGap >= 0 ? '+' : '−'}{fmtYen(Math.abs(weeklyGap))}
                </dd>
              </div>
              <div><dt>達成率</dt><dd>{weeklyAchievementRate.toFixed(1)}%</dd></div>
              <div><dt>1日平均売上</dt><dd>{eligibleDays.length ? fmtYen(latestWeek.sales / eligibleDays.length) : '—'}</dd></div>
              <div><dt>日別達成</dt><dd>{achievedDays}<em>／{eligibleDays.length}日</em></dd></div>
            </dl>
          </section>

          <section className="kpi-weekly-chart-panel">
            <div className="kpi-weekly-chart-heading">
              <div>
                <span>TUESDAY — SUNDAY</span>
                <h2>曜日別・時間別の売上と来客数</h2>
              </div>
              <p>月曜日は定休日のため表示していません</p>
            </div>
            <div className="kpi-weekly-legend" aria-label="グラフの凡例">
              <span><i className="kpi-weekly-legend-sales" />売上</span>
              <span><i className="kpi-weekly-legend-customers" />来客数</span>
              <span><i className="kpi-weekly-legend-target" />時間あたり最低目標</span>
            </div>

            <div className="kpi-weekly-chart-grid">
              {weeklySeries.map((week) => (
                <article className="kpi-weekly-chart-card" key={week.from}>
                  <header>
                    <div>
                      <span>{week.weekday}曜日</span>
                      <h3>{longDate(week.from)}（{week.weekday}）</h3>
                    </div>
                    {week.supplemented && <b>前週から補完</b>}
                    {week.eventTitle && <b className="kpi-event-badge">{week.eventTitle}・KPI除外</b>}
                  </header>
                  {week.eventTitle ? <div className="kpi-event-excluded">イベント日をKPI集計から除外しました<small>売上データは保持されています</small></div> : <><div className="kpi-weekly-card-totals">
                    <span className="kpi-weekly-card-sales">売上 {fmtYen(week.sales)}</span>
                    <span className="kpi-weekly-card-customers">来客 {week.customers.toLocaleString()}人</span>
                    <span className="kpi-weekly-card-unit-price">
                      客単価 {week.customers > 0 ? fmtYen(week.sales / week.customers) : '—'}
                    </span>
                    <span className="kpi-weekly-target-total">
                      日目標 {fmtYen(week.dailyTarget)}（当月{week.operatingDays}営業日） ／ 時間目標 {fmtYen(week.hourlyTarget)}
                    </span>
                  </div>
                  <div className="kpi-weekly-chart" aria-label={`${shortDate(week.from)}から${shortDate(week.to)}までの売上と来客数の折れ線グラフ`}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={week.points} margin={{ top: 12, right: 2, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="2 5" vertical={false} />
                        <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#65605d', fontSize: 11 }} />
                        <YAxis
                          yAxisId="sales"
                          domain={[0, salesAxisMax]}
                          axisLine={false}
                          tickLine={false}
                          width={52}
                          tickCount={4}
                          tick={{ fill: '#65605d', fontSize: 9 }}
                          tickFormatter={(value) => `¥${Math.round(Number(value) / 1000)}k`}
                        />
                        <YAxis
                          yAxisId="customers"
                          domain={[0, customersAxisMax]}
                          orientation="right"
                          axisLine={false}
                          tickLine={false}
                          width={30}
                          tickCount={4}
                          allowDecimals={false}
                          tick={{ fill: '#65605d', fontSize: 9 }}
                          tickFormatter={(value) => `${Math.round(Number(value))}人`}
                        />
                        <ReferenceLine
                          yAxisId="sales"
                          y={week.hourlyTarget}
                          stroke="#b56a00"
                          strokeWidth={2}
                          strokeDasharray="7 5"
                          ifOverflow="extendDomain"
                        />
                        <Tooltip
                          labelFormatter={(label, payload) => {
                            const point = payload?.[0]?.payload as DailyPoint | undefined;
                            return point ? `${longDate(point.date)}（${label}）` : String(label);
                          }}
                          formatter={(value, name) => [
                            name === '売上' ? fmtYen(Number(value)) : `${Number(value).toLocaleString()}人`,
                            name,
                          ]}
                        />
                        <Line
                          yAxisId="sales"
                          type="monotone"
                          dataKey="sales"
                          name="売上"
                          stroke="#0088b0"
                          strokeWidth={2.5}
                          dot={{ r: 3, fill: '#faf9f8', strokeWidth: 2 }}
                          activeDot={{ r: 5 }}
                        />
                        <Line
                          yAxisId="customers"
                          type="monotone"
                          dataKey="customers"
                          name="来客数"
                          stroke="#d6006c"
                          strokeWidth={2.5}
                          dot={{ r: 3, fill: '#faf9f8', strokeWidth: 2 }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div></>}
                </article>
              ))}
            </div>

            <h3 className="kpi-weekly-days-title">表示6日間の日別実績</h3>
            <div className="kpi-weekly-days" aria-label="表示6日間の日別実績">
              {latestWeek.points.map((point) => (
                <article key={point.targetDate}>
                  <div>
                    <strong>{point.day}</strong>
                    <span>{point.dateLabel}</span>
                    <span className={`kpi-weekly-days-status ${point.eventTitle ? '' : point.achieved ? 'kpi-weekly-days-status-achieved' : 'kpi-weekly-days-status-below'}`}>
                      {point.eventTitle ? 'イベント・除外' : point.achieved ? '✓ 達成' : '△ 未達'}
                    </span>
                  </div>
                  {point.eventTitle ? <p>{point.eventTitle}（KPI集計対象外）</p> : <dl>
                    <div><dt>売上</dt><dd>{fmtYen(point.sales)}</dd></div>
                    <div><dt>日目標</dt><dd>{fmtYen(point.dailyTarget)}</dd></div>
                    <div>
                      <dt>目標差</dt>
                      <dd className={point.targetGap >= 0 ? 'kpi-weekly-days-gap-positive' : 'kpi-weekly-days-gap-negative'}>
                        {point.targetGap >= 0 ? '+' : '−'}{fmtYen(Math.abs(point.targetGap))}
                      </dd>
                    </div>
                    <div><dt>来客</dt><dd>{point.customers.toLocaleString()}人</dd></div>
                  </dl>}
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};
